import { ChangeEvent, useState, WheelEvent } from 'react';
import clsx from 'clsx';

import { Button, ListTable, Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { ConversionUnit } from '@atom/types/schema';

import { EQUALS_COLUMN_WIDTH } from './ConversionUnitsTable';
import {
  DUPLICATE_CONVERSION_ERROR,
  getConversionUnitError,
  getReverseConversion,
} from './conversionUnitUtilities';

import classes from './conversionUnitsTable.module.css';

const { TableCell } = ListTable;
const { MenuItem } = Select;

const outlinedBorder = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.neutral.silver,
  },
};

const styles = {
  // top aligned so the fields and buttons stay level when an error grows the row
  fieldCell: {
    padding: '0.4375rem 0.5rem',
    verticalAlign: 'top',
  },
  equalsCell: {
    padding: '0.4375rem 0',
    verticalAlign: 'top',
    color: colors.neutral.dark,
  },
  buttonsCell: {
    padding: '0.4375rem 1rem',
    verticalAlign: 'top',
  },
  unitOption: {
    textTransform: 'uppercase',
  },
  selectInput: {
    ...outlinedBorder,
    color: colors.neutral.dark,
    textTransform: 'uppercase',
    '& .MuiSelect-select': {
      padding: '0.5rem',
      lineHeight: '1.25rem',
    },
  },
  textFieldInput: {
    ...outlinedBorder,
    color: colors.neutral.dark,
    paddingRight: '0.5rem',
    '& .MuiOutlinedInput-input': {
      padding: '0.5rem',
      lineHeight: '1.25rem',
      height: '1.25rem',
    },
    // the conversion is typed only, so the browser's stepper arrows are hidden
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
      {
        WebkitAppearance: 'none',
        margin: 0,
      },
  },
  // the wrapper collapses helper text to zero height under this selector, which
  // clips the error, so it's overridden with the same selector to let the row grow
  helperText: {
    '&.MuiFormHelperText-root': {
      height: 'auto',
      marginTop: '0.25rem',
      marginLeft: 0,
      whiteSpace: 'normal',
    },
  },
  unitAdornment: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
  },
  button: {
    minWidth: 'auto',
    padding: '0.125rem 0.5rem',
  },
  cancelButton: {
    color: colors.neutral.dim,
  },
};

type ConversionUnitFormProps = {
  /**
   * all rows in the table, used to reject duplicate conversions
   */
  conversionUnits: ConversionUnit[];
  unitOptions: string[];
  baseUnit: string;
  /**
   * the number of table columns the form fills
   */
  columnCount: number;
  /**
   * the row being edited. Omit to add a new row.
   */
  conversionUnit?: ConversionUnit;
  onSubmit: (unit: string, conversionNumber: number) => void;
  onCancel: () => void;
  dataCy?: string;
};

/**
 * the conversion field spans the Conversion column and the one after it, so
 * there's room for the number next to long base units like CONTAINER
 */
export const CONVERSION_COL_SPAN = 2;

/**
 * a focused number input changes its value on scroll. React's wheel listener is
 * passive, so it can't be cancelled; blurring stops the change and lets the page scroll.
 */
const blurOnWheel = (event: WheelEvent<HTMLInputElement>) =>
  event.currentTarget.blur();

const renderUnitValue = (value: string) =>
  value ? (
    <>
      1 <span className={classes.unit}>{value}</span>
    </>
  ) : (
    <span className={classes.unitPlaceholder}>Select</span>
  );

/**
 * renders table cells, so it must be placed inside a ConversionUnitsTable row
 */
const ConversionUnitForm = ({
  conversionUnits,
  unitOptions,
  baseUnit,
  columnCount,
  conversionUnit,
  onSubmit,
  onCancel,
  dataCy = 'conversionUnitForm',
}: ConversionUnitFormProps) => {
  const [unit, setUnit] = useState<string>(conversionUnit?.unit ?? '');
  const [conversionNumber, setConversionNumber] = useState<string>(
    conversionUnit ? String(conversionUnit.conversionNumber) : '',
  );

  const parsedConversionNumber =
    conversionNumber === '' ? null : Number(conversionNumber);
  const error =
    conversionNumber === ''
      ? null
      : getConversionUnitError(
          conversionUnits,
          unit,
          parsedConversionNumber,
          conversionUnit?.id,
        );
  // a duplicate is about the unit, so it shows under the dropdown instead
  const unitError = error === DUPLICATE_CONVERSION_ERROR ? error : null;
  const conversionError = unitError ? null : error;
  const isSubmitDisabled = !unit || conversionNumber === '' || !!error;
  const hint = isSubmitDisabled
    ? null
    : `1 ${baseUnit} = ${getReverseConversion(parsedConversionNumber)} ${unit}`;

  return (
    <>
      <TableCell style={styles.fieldCell}>
        <div className={classes.unitField}>
          <Select
            variant="outlined"
            fullWidth
            inputSx={styles.selectInput}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUnit(event.target.value)
            }
            value={unit}
            displayEmpty
            renderValue={renderUnitValue}
            data-cy={`${dataCy}UnitSelect`}
          >
            {unitOptions.map(option => (
              <MenuItem key={option} value={option} style={styles.unitOption}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {unitError && (
            <div
              className={clsx(classes.unitSubtext, classes.unitError)}
              role="alert"
              data-cy={`${dataCy}UnitError`}
            >
              {unitError}
            </div>
          )}
          {hint && (
            <div
              className={clsx(classes.unitSubtext, classes.conversionHint)}
              data-cy={`${dataCy}ConversionHint`}
            >
              {hint}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell center width={EQUALS_COLUMN_WIDTH} style={styles.equalsCell}>
        <div className={classes.equalsSign}>=</div>
      </TableCell>
      <TableCell colSpan={CONVERSION_COL_SPAN} style={styles.fieldCell}>
        <TextField
          variant="outlined"
          value={conversionNumber}
          type="number"
          fullWidth
          error={!!conversionError}
          helperText={conversionError}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setConversionNumber(event.target.value)
          }
          slotProps={{
            input: {
              sx: styles.textFieldInput,
              endAdornment: (
                <span style={styles.unitAdornment}>{baseUnit}</span>
              ),
            },
            formHelperText: { sx: styles.helperText },
            htmlInput: { 'aria-label': 'Conversion', onWheel: blurOnWheel },
          }}
          data-cy={`${dataCy}ConversionInput`}
        />
      </TableCell>
      <TableCell
        colSpan={columnCount - 2 - CONVERSION_COL_SPAN}
        style={styles.buttonsCell}
      >
        <div className={classes.formButtons}>
          <Button
            variant="text"
            sx={{ ...styles.button, ...styles.cancelButton }}
            onClick={onCancel}
            data-cy={`${dataCy}CancelButton`}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="text"
            sx={styles.button}
            disabled={isSubmitDisabled}
            onClick={() => onSubmit(unit, parsedConversionNumber)}
            data-cy={`${dataCy}SubmitButton`}
          >
            {conversionUnit ? 'Save' : 'Add'}
          </Button>
        </div>
      </TableCell>
    </>
  );
};

export default ConversionUnitForm;
